import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import { Tag } from '@lego/klik-ui';
import { AssessmentInfoRow } from './AssessmentInfoRow';
import { AssessmentStatusIndicator } from './AssessmentStatusIndicator';
import { AssessmentDateLabel } from './AssessmentDateLabel';
import { formatDate } from '../utils/formatDate';
import { spacing, colors, media, font } from '../config';
import { uiFriendlyRolesAccessors } from '../constants/uiFriendlyRoles';
import { relevantStatusForYoy } from '../constants/assessmentStatus';

const assessmentInfoContainerStyle = css({
  display: 'flex',
  backgroundColor: colors.neutralColors.slate02,
  border: `1px solid ${colors.neutralColors.slate20}`,
  marginTop: spacing.size7,
  padding: spacing.size4,
  flexWrap: 'wrap',
  [media.medium]: {
    padding: spacing.size10,
  },
});

const assessmentInfoColumnStyle = css({
  flexGrow: 1,
});

const customerNameStyle = css({
  color: colors.boldColors.lightBlue.lightBlue80,
  fontSize: font.size.size2,
});

const assignmentInfoHeaderStyle = css({
  display: 'flex',
  width: '100%',
});

const versionTagStyle = css({
  marginLeft: 'auto',
});

const AssessmentInfoSection = ({ assessmentInfo }) => {
  const {
    contactEmails,
    version,
    assessmentDate,
    approvalDate,
    assessmentStatus,
    reviewers,
    approvers,
    pendingUser,
    performers,
    customerDetail: { id, name, c6Id, c6Name, channel, businessUnit, omni },
    surveyState: { score },
    lastYearScore: previousAssessmentScore,
  } = assessmentInfo;

  const yoyChange =
    previousAssessmentScore !== null &&
    previousAssessmentScore !== undefined &&
    relevantStatusForYoy(assessmentStatus)
      ? score - previousAssessmentScore
      : undefined;

  return (
    <div>
      <div css={assessmentInfoContainerStyle}>
        <div css={assignmentInfoHeaderStyle}>
          <h3 css={customerNameStyle}>{name}</h3>
          <Tag css={versionTagStyle}>
            <Tag.Label>v.{version}</Tag.Label>
          </Tag>
        </div>
        <div css={assessmentInfoColumnStyle}>
          <AssessmentInfoRow label="BU" value={businessUnit} />
          <AssessmentInfoRow label="C6 ID" value={c6Id} />
          <AssessmentInfoRow label="C6" value={c6Name} />
          <AssessmentInfoRow label="Customer ID" value={id} />
          <AssessmentInfoRow label="Customer Channel" value={channel} />
          <AssessmentInfoRow label="Omni" value={omni} />
          {contactEmails && contactEmails.length > 0 && (
            <AssessmentInfoRow
              label="Emails"
              value={contactEmails.join('\n')}
            />
          )}
        </div>
        <div css={assessmentInfoColumnStyle}>
          <AssessmentInfoRow
            label="Status"
            value={<AssessmentStatusIndicator status={assessmentStatus} />}
          />
          <AssessmentInfoRow
            label="Score"
            value={`${score}% ${
              yoyChange !== undefined
                ? `(YoY ${yoyChange > 0 ? '+' : ''}${yoyChange}%)`
                : ''
            }`}
            bold
          />
          <AssessmentInfoRow
            label="Last Year Score"
            value={`${
              previousAssessmentScore !== null &&
              previousAssessmentScore !== undefined
                ? `${previousAssessmentScore}%`
                : 'Not available'
            }`}
            bold={!!previousAssessmentScore}
          />
          <AssessmentInfoRow
            label="Next Action By"
            value={uiFriendlyRolesAccessors(pendingUser)}
          />
          <AssessmentInfoRow label="Performers" value={performers.join('\n')} />
          <AssessmentInfoRow label="Reviewers" value={reviewers.join('\n')} />
          <AssessmentInfoRow label="Approvers" value={approvers.join('\n')} />
          <AssessmentInfoRow
            label={
              <AssessmentDateLabel
                label="Assessment Date"
                dateFormat="(dd.mm.yyyy)"
              />
            }
            value={assessmentDate ? formatDate(new Date(assessmentDate)) : ''}
          />
          <AssessmentInfoRow
            label={
              <AssessmentDateLabel
                label="Approval Date"
                dateFormat="(dd.mm.yyyy)"
              />
            }
            value={approvalDate ? formatDate(new Date(approvalDate)) : ''}
          />
        </div>
      </div>
    </div>
  );
};

AssessmentInfoSection.propTypes = {
  assessmentInfo: PropTypes.object.isRequired,
};

export { AssessmentInfoSection };
